import * as React from 'react';
import { ReactComponent as BarChartMenuIcon } from '../assets/icons/BarChart.svg';
import { authorizedRolesCombinations } from '../components/roles/roles';
import { ROLE_TYPES } from './DataConstants';
import { PERMISSION_NAMES, isUserManagedBySupplier } from './AuthUtils';
import { COMPLIANCE_FEATURE_FLAG } from '../components/compliance_common/ComplianceConstants';
import {
	isSupplierPayingCustomer,
	isSupplierPayingCustomerAndAdmin,
	nullSafeGet,
} from './DataAccessUtils';
import posthog from 'posthog-js';

/** Sidebar nav: use `icon` for icons8 webfont class, or `menuSvgIcon` for an imported SVG (`ReactComponent`). */
export interface SidebarNavMenuIconConfig {
	icon?: string;
	menuSvgIcon?: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
}

export const STRIPE_TEST_API_KEY = 'pk_test_tydO9GGsVzeDP5bXXuA6OvlL';
export const STRIPE_LIVE_API_KEY = 'pk_live_kT83xmAK8smH4hEGydsUezgF';

export const RELEASE_VERSION = 1.0;

export const BACKEND_URI_BY_ENV = {
	RELEASE: process.env.REACT_APP_BACKEND_URI || 'http://localhost:3000',
	DEV: 'http://api-dev.useopenwrench.com',
	TEST: 'https://api-test.useopenwrench.com',
	PROD: 'https://api.useopenwrench.com',
};

// THIS NEEDS TO BE CHANGED IF YOU DON'T WANT TO RUN AGAINST PROD
export const CURRENT_ENV = process.env.REACT_APP_CURRENT_ENV || 'PROD';
// THIS SHOULD BE CHANGED DEPENDING ON IF YOU ARE PUSHING TO BUYER OR SUPPLIER SIDE OF WEB APP
export const CURRENT_ENV_ROLE_TYPE =
	ROLE_TYPES[process.env.REACT_APP_CURRENT_ENV_ROLE_TYPE] || ROLE_TYPES.SUPPLIER;

export const BACKEND_ENV_NAMES = new Set(Object.keys(BACKEND_URI_BY_ENV));

export const getBackendUriWithEnv = (env) => {
	if (!BACKEND_ENV_NAMES.has(env)) {
		throw new Error(`Invalid environment name provided to RestCrudThunksGenerator: ${env}`);
	}
	return BACKEND_URI_BY_ENV[env];
};

export const getBackendUri = () => BACKEND_URI_BY_ENV[CURRENT_ENV];

export const BACKEND_WSS_URI_BY_ENV = {
	RELEASE: process.env.REACT_APP_WSS_BACKEND_URI || 'ws://localhost:3000',
	DEV: 'ws://api-dev.useopenwrench.com',
	TEST: 'wss://api-test.useopenwrench.com',
	PROD: 'wss://api.useopenwrench.com',
};

export const getWSSBackendUri = () => BACKEND_WSS_URI_BY_ENV[CURRENT_ENV];

// ow-agent-harness-service — the in-app AI assistant backend. Its hostname must
// stay a sibling subdomain of the app: the chat panel authenticates with a
// same-site cookie, which is what keeps third-party cookie blocking out of the
// picture. See src/thunks/agent_harness_thunks.ts.
export const AGENT_HARNESS_URI_BY_ENV = {
	RELEASE: process.env.REACT_APP_AGENT_HARNESS_URI || 'http://localhost:3000',
	DEV: 'https://agent-test.useopenwrench.com',
	TEST: 'https://agent.useopenwrench.com', // @todo : fix this to point to a test harness once we have one, so that we can test the agent without affecting PROD data
	PROD: 'https://agent.useopenwrench.com',
};

// REACT_APP_AGENT_HARNESS_URI overrides in any environment, not just RELEASE, so
// a locally-run harness can be targeted without also switching CURRENT_ENV and
// repointing the OpenWrench backend. Normal local work needs no override: run
// the dev server as app-local.useopenwrench.com (yarn start:agent) and the PROD
// default is already same-site with it. See src/ai_agent/README.md.
export const getAgentHarnessUri = () =>
	process.env.REACT_APP_AGENT_HARNESS_URI || AGENT_HARNESS_URI_BY_ENV[CURRENT_ENV];

// openwrench-intelligence-service — Radar's procedure authoring API, under
// /api/v1/buyer/agent. It is its own deployment, not a route on the Play
// backend, and authenticates with the buyer app's X-Auth-Token.
//
// PROD only, because PROD is all there is: intelligence-test, -dev and
// -staging do not resolve. The service sends CORS headers and allows
// X-Auth-Token, but echoes only the deployed app origins — app and partners.
//
// So TEST stays empty on purpose rather than borrowing PROD's host the way
// AGENT_HARNESS_URI_BY_ENV does above. A test build pointed here would be
// refused at the preflight, since app-test is not on that allowlist, and would
// be writing PROD Radar data if it ever were.
//
// Empty does not disable anything, though: it only makes the paths relative,
// so a direct route still fires them at the app's own origin and they fail
// there. What stops them being made at all is the `isRadarEnabled()` gate in
// FeatureFlagUtils. The two are easy to conflate — this map decides *where* a
// request goes, the flag decides *whether* one happens.
//
// Local work: run it the way the harness expects. `yarn start:agent` serves the
// app as https://app-local.useopenwrench.com:3000, which IS on the allowlist,
// so set REACT_APP_AGENT_INTELLIGENCE_URI and the calls go direct. Or set
// REACT_APP_CURRENT_ENV=TEST to get the empty entry and let the dev-server
// proxy forward them (src/setupProxy.js, untracked).
//
// Plain `yarn start` reaches neither: CURRENT_ENV falls back to PROD, so this
// hands back the real host, the request skips the proxy, and localhost:3000 is
// not allowlisted. That is left as it is rather than sniffing the hostname
// here — set one of the two variables above.
export const AGENT_INTELLIGENCE_URI_BY_ENV = {
	RELEASE: '',
	DEV: '',
	TEST: '',
	PROD: 'https://intelligence.useopenwrench.com',
};

/**
 * Empty means "ask the app's own origin" — what the dev-server proxy listens
 * for locally, and on a deployed build a path CloudFront answers rather than
 * the service. It is the `isRadarEnabled()` gate, not this, that keeps an
 * unconfigured build from asking in the first place.
 *
 * REACT_APP_AGENT_INTELLIGENCE_URI overrides in any environment, as the harness
 * variable does, so a locally-run service can be targeted without switching
 * CURRENT_ENV.
 */
export const getAgentIntelligenceUri = () =>
	process.env.REACT_APP_AGENT_INTELLIGENCE_URI || AGENT_INTELLIGENCE_URI_BY_ENV[CURRENT_ENV] || '';

// Gates Oscar's launcher tab (bottom-right edge) per workspace while it is
// rolled out. There is no sidebar entry — the tab is the way in, and /buyer/agent
// and /supplier/agent stay reachable by URL regardless.

export const AGENT_CHAT_FEATURE_FLAG = 'agent-chat';
// Gates Oscar's page context on top of `agent-chat`: the record sent with each
// message, the dock's context bar, and the Ask Oscar inputs on record pages.
export const OSCAR_CONTEXT_FEATURE_FLAG = 'oscar-context';
// Completion Summary (AI resolution summary) on the work order Overview tab.
export const WO_SUMMARY_FEATURE_FLAG = 'wo-summary';

/**
 * Radar, while it is rolled out. Gates the Agent Operating Procedures menu
 * entry; the routes stay reachable by URL, as Oscar's do.
 */
export const RADAR_FEATURE_FLAG = 'radar';

export const BUYER_MENU_ITEMS = [
	{
		to: '/buyer/dashboards',
		title: 'Dashboard',
		icon: 'icons8-binoculars',
		authorizedRoles: authorizedRolesCombinations.BUYER_ONLY,
		checkActive: (path) => new RegExp('/buyer/dashboards').test(path),
	},
	{
		to: '/buyer/workOrders',
		title: 'Work Orders',
		icon: 'icons8-wrench',
		authorizedRoles: authorizedRolesCombinations.BUYER_ONLY,
		checkActive: (path) => new RegExp('/buyer/workOrders').test(path),
	},
	{
		to: '/buyer/inbox',
		title: 'Inbox',
		icon: 'icons8-mailbox',
		authorizedRoles: authorizedRolesCombinations.BUYER_ONLY,
		checkActive: (path) => new RegExp('/buyer/inbox').test(path),
		count: (props = {}) => nullSafeGet('userNotifications.counts.TASKS_NOTIFICATIONS', props),
	},
	{
		to: '/buyer/projects',
		title: 'Projects',
		icon: 'icons8-blueprint',
		lockedIfManagedBySupplier: true,
		authorizedRoles: authorizedRolesCombinations.BUYER_ONLY_NO_STORE_ASSOCIATE,
		checkActive: (path) => new RegExp('/buyer/projects').test(path),
	},
	{
		to: '/buyer/requestsForProposal',
		title: 'RFPs',
		icon: 'icons8-cash-in-hand',
		lockedIfManagedBySupplier: true,
		authorizedRoles: authorizedRolesCombinations.BUYER_ONLY_NO_STORE_ASSOCIATE,
		checkActive: (path) => new RegExp('/buyer/requestsForProposal').test(path),
	},
	{
		to: '/buyer/siteSurveys',
		title: 'Site Surveys',
		icon: 'icons8-clipboard-checklist',
		lockedIfManagedBySupplier: true,
		authorizedRoles: authorizedRolesCombinations.BUYER_ONLY,
		checkActive: (path) => new RegExp('/buyer/siteSurveys').test(path),
	},
	{
		to: '/buyer/plannedMaintenance',
		title: 'PMs',
		icon: 'icons8-refresh',
		lockedIfManagedBySupplier: true,
		authorizedRoles: authorizedRolesCombinations.BUYER_ONLY_NO_STORE_ASSOCIATE,
		checkActive: (path) => new RegExp('/buyer/plannedMaintenance').test(path),
	},
	{
		to: '/buyer/quotes',
		title: 'Quotes',
		icon: 'icons8-billing-machine',
		accessPermissionName: PERMISSION_NAMES.CAN_VIEW_QUOTES,
		authorizedRoles: authorizedRolesCombinations.BUYER_ONLY_NO_STORE_ASSOCIATE,
		checkActive: (path) => new RegExp('/buyer/quotes').test(path),
	},
	{
		to: '/buyer/invoices',
		title: 'Invoices',
		icon: 'icons8-invoice',
		accessPermissionName: PERMISSION_NAMES.CAN_VIEW_INVOICES,
		authorizedRoles: authorizedRolesCombinations.BUYER_ONLY_NO_STORE_ASSOCIATE,
		checkActive: (path) => new RegExp('/buyer/invoices').test(path),
	},
	{
		to: '/buyer/assets',
		title: 'Assets',
		icon: 'icons8-bulldozer',
		authorizedRoles: authorizedRolesCombinations.BUYER_ONLY,
		checkActive: (path) => new RegExp('/buyer/assets').test(path),
	},
	{
		to: '/buyer/meters',
		title: 'Meters',
		icon: 'icons8-dashboard',
		lockedIfManagedBySupplier: true,
		authorizedRoles: authorizedRolesCombinations.BUYER_ONLY,
		checkActive: (path) => new RegExp('/buyer/meters').test(path),
	},
	{
		to: '/buyer/locations',
		title: 'Locations',
		icon: 'icons8-store',
		authorizedRoles: authorizedRolesCombinations.BUYER_ONLY,
		checkActive: (path) => new RegExp('/buyer/locations').test(path),
	},
	{
		to: '/buyer/suppliers',
		title: 'Service Providers',
		icon: 'icons8-worker',
		lockedIfManagedBySupplier: true,
		// Visibility is governed by canAccessSupplierPage; keep the role gate buyer-wide so a store
		// associate granted the permission can see it (default stays managers-only via the permission).
		accessPermissionName: PERMISSION_NAMES.CAN_ACCESS_SUPPLIER_PAGE,
		authorizedRoles: authorizedRolesCombinations.BUYER_ONLY,
		checkActive: (path) => new RegExp('/buyer/suppliers').test(path),
	},
	{
		to: '',
		title: 'Analytics',
		icon: 'icons8-line-chart',
		accessPermissionName: PERMISSION_NAMES.ACCESS_ANALYTICS,
		authorizedRoles: authorizedRolesCombinations.BUYER_ONLY,
		children: [
			{
				to: '/buyer/analytics/lifecycle',
				title: 'Lifecycle',
				menuSvgIcon: BarChartMenuIcon,
				lockedIfManagedBySupplier: true,
				authorizedRoles: authorizedRolesCombinations.BUYER_ONLY_NO_STORE_ASSOCIATE,
				checkActive: (path) => new RegExp('/buyer/analytics/lifecycle').test(path),
				showInMenu: () => posthog.isFeatureEnabled('life-cycle-analytics'),
			},
			{
				to: '/buyer/analytics/spend',
				title: 'Spend',
				icon: 'icons8-fund-accounting',
				authorizedRoles: authorizedRolesCombinations.BUYER_ONLY,
				checkActive: (path) => new RegExp('/buyer/analytics/spend').test(path),
			},
			{
				to: '/buyer/analytics/work',
				title: 'Work',
				icon: 'icons8-chart',
				authorizedRoles: authorizedRolesCombinations.BUYER_ONLY,
				checkActive: (path) => new RegExp('/buyer/analytics/work').test(path),
			},
			{
				to: '/buyer/analytics/kpi',
				title: 'Scorecards',
				icon: 'icons8-scorecard',
				lockedIfManagedBySupplier: true,
				authorizedRoles: authorizedRolesCombinations.BUYER_ONLY_NO_STORE_ASSOCIATE,
				checkActive: (path) => new RegExp('/buyer/analytics/kpi').test(path),
			},
			{
				to: '/buyer/analytics/assets',
				title: 'Downtime',
				icon: 'icons8-sand-watch',
				lockedIfManagedBySupplier: true,
				authorizedRoles: authorizedRolesCombinations.BUYER_ONLY_NO_STORE_ASSOCIATE,
				checkActive: (path) => new RegExp('/buyer/analytics/assets').test(path),
			},
		],
	},
	{
		to: '',
		title: 'Workflows',
		icon: 'icons8-mind-map',
		anyAccessPermissionNames: [PERMISSION_NAMES.VIEW_TROUBLESHOOTING, PERMISSION_NAMES.VIEW_BUDGET],
		authorizedRoles: authorizedRolesCombinations.BUYER_ONLY,
		children: [
			{
				to: '/buyer/workflows/agent_operating_procedures',
				title: 'Agent Operating Procedures',
				// Not the mind map, which is the Workflows group's own: a child
				// wearing its parent's icon tells a reader nothing about which of
				// the two they are looking at.
				icon: 'icons8-ai',
				showInMenu: () => posthog.isFeatureEnabled(RADAR_FEATURE_FLAG),
				authorizedRoles: authorizedRolesCombinations.BUYER_ADMIN_ONLY,
				checkActive: (path) => new RegExp('/buyer/workflows/agent_operating_procedures').test(path),
			},
			{
				to: '/buyer/workflows/troubleshooting',
				title: 'Troubleshooting',
				icon: 'icons8-question-mark-in-a-chat-bubble',
				lockedIfManagedBySupplier: true,
				accessPermissionName: PERMISSION_NAMES.VIEW_TROUBLESHOOTING,
				authorizedRoles: authorizedRolesCombinations.BUYER_ONLY,
				checkActive: (path) => new RegExp('/buyer/workflows/troubleshooting').test(path),
			},
			{
				to: '/buyer/workflows/budgets',
				title: 'Budgets',
				icon: 'icons8-cash-in-hand',
				accessPermissionName: PERMISSION_NAMES.VIEW_BUDGET,
				authorizedRoles: authorizedRolesCombinations.BUYER_ONLY,
				checkActive: (path) => new RegExp('/buyer/workflows/budgets').test(path),
			},
			{
				to: '/buyer/workflows/cycleevents',
				title: 'Asset Cycle Count',
				icon: 'icons8-refresh',
				authorizedRoles: authorizedRolesCombinations.BUYER_ADMIN_ONLY,
				showInMenu: (currentUser) => !isUserManagedBySupplier(currentUser),
				checkActive: (path) => new RegExp('/buyer/workflows/cycleevents').test(path),
			},
		],
	},
	{
		to: '/buyer/admin',
		title: 'Admin',
		icon: 'icons8-multiple-users',
		authorizedRoles: authorizedRolesCombinations.BUYER_ADMIN_ONLY,
		checkActive: (path) => new RegExp('/buyer/admin').test(path),
	},
	{
		to: '/buyer/approvalHierarchies',
		title: 'Approvals',
		icon: 'icons8-checkmark',
		lockedIfManagedBySupplier: true,
		authorizedRoles: authorizedRolesCombinations.BUYER_ADMIN_ONLY,
		checkActive: (path) => new RegExp('/buyer/approvalHierarchies').test(path),
	},
];

export const SUPPLIER_MENU_ITEMS = [
	{
		to: '/supplier/dashboards',
		title: 'Dashboard',
		icon: 'icons8-binoculars',
		authorizedRoles: authorizedRolesCombinations.SUPPLIER_ONLY,
		checkActive: (path) => new RegExp('/supplier/dashboards').test(path),
	},
	{
		to: '/supplier/workOrders',
		title: 'Work Orders',
		icon: 'icons8-wrench',
		authorizedRoles: authorizedRolesCombinations.SUPPLIER_ONLY,
		checkActive: (path) => new RegExp('/supplier/workOrders').test(path),
	},
	{
		to: '/supplier/inbox',
		title: 'Inbox',
		icon: 'icons8-mailbox',
		authorizedRoles: authorizedRolesCombinations.SUPPLIER_ONLY,
		checkActive: (path) => new RegExp('/supplier/inbox').test(path),
		count: (props = {}) => nullSafeGet('userNotifications.counts.TASKS_NOTIFICATIONS', props),
	},
	{
		to: '/supplier/walkthroughs',
		title: 'Walkthroughs',
		icon: 'icons8-clipboard-checklist',
		authorizedRoles: authorizedRolesCombinations.SUPER_ADMIN_ONLY,
		unauthorizedCompanies: new Set([276322]),
		checkActive: (path) => new RegExp('/supplier/walkthroughs').test(path),
	},
	{
		to: '/supplier/opportunities',
		title: 'RFPs',
		icon: 'icons8-cash-in-hand',
		authorizedRoles: authorizedRolesCombinations.SUPPLIER_ONLY,
		checkActive: (path) => new RegExp('/supplier/opportunities').test(path),
	},
	{
		to: '/supplier/quotes',
		title: 'Quotes',
		icon: 'icons8-billing-machine',
		accessPermissionName: PERMISSION_NAMES.CAN_VIEW_QUOTES,
		authorizedRoles: authorizedRolesCombinations.SUPPLIER_ONLY,
		checkActive: (path) => new RegExp('/supplier/quotes').test(path),
		showInMenu: (currentUser) => !isSupplierPayingCustomer(currentUser),
	},
	{
		to: '/supplier/quotes',
		title: 'Quotes',
		icon: 'icons8-billing-machine',
		accessPermissionName: PERMISSION_NAMES.CAN_VIEW_QUOTES,
		authorizedRoles: authorizedRolesCombinations.SUPPLIER_ONLY,
		checkActive: (path) => new RegExp('/supplier/quotes').test(path),
		showInMenu: (currentUser) => isSupplierPayingCustomer(currentUser),
		children: [
			{
				to: '/supplier/quotes',
				title: 'Client',
				icon: 'icons8-warehouse-3',
				authorizedRoles: authorizedRolesCombinations.SUPPLIER_ONLY,
				unauthorizedCompanies: new Set([276322]),
				checkActive: (path) =>
					new RegExp('/supplier/quotes').test(path) &&
					!new RegExp('/supplier/quotes/subcontractor').test(path) &&
					!new RegExp('/supplier/quotes/contractor').test(path),
			},
			{
				to: '/supplier/quotes/subcontractor',
				title: 'Subcontractor',
				icon: 'icons8-contractor',
				authorizedRoles: authorizedRolesCombinations.SUPPLIER_ONLY,
				unauthorizedCompanies: new Set([276322]),
				checkActive: (path) =>
					new RegExp('/supplier/quotes/subcontractor').test(path) ||
					new RegExp('/supplier/quotes/contractor').test(path),
			},
		],
	},
	{
		to: '/supplier/invoices',
		title: 'Invoices',
		icon: 'icons8-invoice',
		accessPermissionName: PERMISSION_NAMES.CAN_VIEW_INVOICES,
		authorizedRoles: authorizedRolesCombinations.SUPPLIER_ONLY,
		checkActive: (path) => new RegExp('/supplier/invoices').test(path),
		showInMenu: (currentUser) => !isSupplierPayingCustomer(currentUser),
	},
	{
		to: '/supplier/invoices',
		title: 'Invoices',
		icon: 'icons8-invoice',
		accessPermissionName: PERMISSION_NAMES.CAN_VIEW_INVOICES,
		authorizedRoles: authorizedRolesCombinations.SUPPLIER_ONLY,
		checkActive: (path) => new RegExp('/supplier/invoices').test(path),
		showInMenu: (currentUser) => isSupplierPayingCustomer(currentUser),
		children: [
			{
				to: '/supplier/invoices',
				title: 'Client',
				icon: 'icons8-warehouse-3',
				authorizedRoles: authorizedRolesCombinations.SUPPLIER_ONLY,
				unauthorizedCompanies: new Set([276322]),
				checkActive: (path) =>
					new RegExp('/supplier/invoices').test(path) &&
					!new RegExp('/supplier/invoices/subcontractor').test(path) &&
					!new RegExp('/supplier/invoices/contractor').test(path),
			},
			{
				to: '/supplier/invoices/subcontractor',
				title: 'Subcontractor',
				icon: 'icons8-contractor',
				authorizedRoles: authorizedRolesCombinations.SUPPLIER_ONLY,
				unauthorizedCompanies: new Set([276322]),
				checkActive: (path) =>
					new RegExp('/supplier/invoices/subcontractor').test(path) ||
					new RegExp('/supplier/invoices/contractor').test(path),
			},
		],
	},
	{
		to: '/supplier/clients',
		title: 'Routing',
		icon: 'icons8-business-building-2',
		authorizedRoles: authorizedRolesCombinations.SUPPLIER_ONLY,
		checkActive: (path) => new RegExp('/supplier/clients').test(path),
		showInMenu: (currentUser) => !isSupplierPayingCustomer(currentUser),
	},
	{
		to: '/supplier/contractor_clients',
		title: 'Clients',
		icon: 'icons8-store',
		authorizedRoles: authorizedRolesCombinations.SUPPLIER_ONLY,
		checkActive: (path) => new RegExp('/supplier/contractor_clients').test(path),
		// Paying suppliers get the All / All-Locations tabs; non-paying suppliers only
		// have the free Compliance tab here, so hide "Clients" entirely for them when
		// the compliance feature is off.
		showInMenu: (currentUser) =>
			isSupplierPayingCustomer(currentUser) || posthog.isFeatureEnabled(COMPLIANCE_FEATURE_FLAG),
	},
	{
		to: '/supplier/contractors',
		title: 'Subcontractors',
		icon: 'icons8-company-2',
		authorizedRoles: authorizedRolesCombinations.SUPPLIER_ONLY,
		checkActive: (path) => new RegExp('/supplier/contractors').test(path),
		showInMenu: (currentUser) => isSupplierPayingCustomerAndAdmin(currentUser),
	},
	{
		to: '/supplier/fieldTechs',
		title: 'Field Techs',
		icon: 'icons8-street-worker',
		authorizedRoles: authorizedRolesCombinations.SUPPLIER_ONLY,
		checkActive: (path) => new RegExp('/supplier/fieldTechs').test(path),
	},
	{
		to: '',
		title: 'Analytics',
		icon: 'icons8-chart',
		authorizedRoles: authorizedRolesCombinations.SUPPLIER_ONLY,
		unauthorizedCompanies: new Set([276322]),
		checkActive: (path) => new RegExp('/supplier/analytics').test(path),
		children: [
			{
				to: '/supplier/analytics/fieldTechAnalytics',
				title: 'Tech Scorecards',
				icon: 'icons8-chart',
				authorizedRoles: authorizedRolesCombinations.SUPPLIER_ONLY,
				unauthorizedCompanies: new Set([276322]),
				checkActive: (path) => new RegExp('/supplier/analytics/fieldTechAnalytics').test(path),
			},
		],
	},
	{
		to: '',
		title: 'Inventory',
		icon: 'icons8-warehouse-3',
		authorizedRoles: authorizedRolesCombinations.SUPPLIER_ONLY,
		unauthorizedCompanies: new Set([276322]),
		checkActive: (path) =>
			new RegExp(
				'/supplier/(stockLocations|parts|equipments|purchaseRequests|purchaseOrders|vendors|tools)'
			).test(path),
		children: [
			{
				to: '/supplier/stockLocations',
				title: 'Stock Locations',
				icon: 'icons8-warehouse-3',
				authorizedRoles: authorizedRolesCombinations.SUPPLIER_ONLY,
				unauthorizedCompanies: new Set([276322]),
				checkActive: (path) => new RegExp('/supplier/stockLocations').test(path),
			},
			{
				to: '/supplier/parts',
				title: 'Parts',
				icon: 'icons8-screw',
				authorizedRoles: authorizedRolesCombinations.SUPPLIER_ONLY,
				unauthorizedCompanies: new Set([276322]),
				checkActive: (path) => new RegExp('/supplier/parts').test(path),
			},
			{
				to: '/supplier/equipments',
				title: 'Equipment',
				icon: 'icons8-drill-2',
				authorizedRoles: authorizedRolesCombinations.SUPPLIER_ONLY,
				unauthorizedCompanies: new Set([276322]),
				checkActive: (path) => new RegExp('/supplier/equipments').test(path),
			},
			{
				to: '/supplier/tools',
				title: 'Tools',
				icon: 'icons8-wrench',
				authorizedRoles: authorizedRolesCombinations.SUPPLIER_ONLY,
				unauthorizedCompanies: new Set([276322]),
				checkActive: (path) => new RegExp('/supplier/tools').test(path),
				showInMenu: () => posthog.isFeatureEnabled('tools'),
			},
			{
				to: '/supplier/purchaseRequests',
				title: 'PRs',
				icon: 'icons8-add-shopping-cart',
				authorizedRoles: authorizedRolesCombinations.SUPPLIER_ONLY,
				accessPermissionName: PERMISSION_NAMES.ACCESS_PR_AND_PO,
				unauthorizedCompanies: new Set([276322]),
				checkActive: (path) => new RegExp('/supplier/purchaseRequests').test(path),
			},
			{
				to: '/supplier/purchaseOrders',
				title: 'POs',
				icon: 'icons8-buying',
				authorizedRoles: authorizedRolesCombinations.SUPPLIER_ONLY,
				accessPermissionName: PERMISSION_NAMES.ACCESS_PR_AND_PO,
				unauthorizedCompanies: new Set([276322]),
				checkActive: (path) => new RegExp('/supplier/purchaseOrders').test(path),
			},
			{
				to: '/supplier/vendors',
				title: 'Vendors',
				icon: 'icons8-shop-2',
				authorizedRoles: authorizedRolesCombinations.SUPPLIER_ONLY,
				unauthorizedCompanies: new Set([276322]),
				checkActive: (path) => new RegExp('/supplier/vendors').test(path),
			},
		],
	},
	{
		to: '/supplier/company/me',
		title: 'Company',
		icon: 'icons8-company-2',
		authorizedRoles: authorizedRolesCombinations.SUPPLIER_ONLY,
		checkActive: (path) => new RegExp('/supplier/company').test(path),
	},
	{
		to: '/supplier/admin',
		title: 'Admin',
		icon: 'icons8-multiple-users',
		authorizedRoles: authorizedRolesCombinations.SUPPLIER_ADMIN_ONLY,
		checkActive: (path) => new RegExp('/supplier/admin').test(path),
	},
];

export const INTERNAL_SUPPLIER_MENU_ITEMS = [
	{
		to: '/supplier/dashboards',
		title: 'Dashboard',
		icon: 'icons8-binoculars',
		authorizedRoles: authorizedRolesCombinations.SUPPLIER_ONLY,
		checkActive: (path) => new RegExp('/supplier/dashboards').test(path),
	},
	{
		to: '/supplier/workOrders',
		title: 'Work Orders',
		icon: 'icons8-wrench',
		authorizedRoles: authorizedRolesCombinations.SUPPLIER_ONLY,
		checkActive: (path) => new RegExp('/supplier/workOrders').test(path),
	},
	{
		to: '/supplier/inbox',
		title: 'Inbox',
		icon: 'icons8-mailbox',
		authorizedRoles: authorizedRolesCombinations.SUPPLIER_ONLY,
		checkActive: (path) => new RegExp('/supplier/inbox').test(path),
		count: (props = {}) => nullSafeGet('userNotifications.counts.TASKS_NOTIFICATIONS', props),
	},
	{
		to: '/supplier/projects',
		title: 'Projects',
		icon: 'icons8-blueprint',
		authorizedRoles: authorizedRolesCombinations.SUPPLIER_ONLY,
		checkActive: (path) => new RegExp('/supplier/projects').test(path),
	},
	{
		to: '/supplier/siteSurveys',
		title: 'Site Surveys',
		icon: 'icons8-clipboard-checklist',
		authorizedRoles: authorizedRolesCombinations.SUPPLIER_ONLY,
		checkActive: (path) => new RegExp('/supplier/siteSurveys').test(path),
	},
	{
		to: '/supplier/plannedMaintenance',
		title: 'PMs',
		icon: 'icons8-refresh',
		authorizedRoles: authorizedRolesCombinations.SUPPLIER_ONLY,
		checkActive: (path) => new RegExp('/supplier/plannedMaintenance').test(path),
	},
	{
		to: '/supplier/requestsForProposal',
		title: 'RFPs',
		icon: 'icons8-cash-in-hand',
		authorizedRoles: authorizedRolesCombinations.SUPPLIER_ONLY,
		checkActive: (path) => new RegExp('/supplier/requestsForProposal').test(path),
	},
	{
		to: '/supplier/quotes',
		title: 'Quotes',
		icon: 'icons8-billing-machine',
		accessPermissionName: PERMISSION_NAMES.CAN_VIEW_QUOTES,
		authorizedRoles: authorizedRolesCombinations.SUPPLIER_ONLY,
		checkActive: (path) => new RegExp('/supplier/quotes').test(path),
	},
	{
		to: '/supplier/invoices',
		title: 'Invoices',
		icon: 'icons8-invoice',
		accessPermissionName: PERMISSION_NAMES.CAN_VIEW_INVOICES,
		authorizedRoles: authorizedRolesCombinations.SUPPLIER_ONLY,
		checkActive: (path) => new RegExp('/supplier/invoices').test(path),
	},
	{
		to: '/supplier/clients',
		title: 'Routing',
		icon: 'icons8-business-building-2',
		authorizedRoles: authorizedRolesCombinations.SUPPLIER_ADMIN_ONLY,
		checkActive: (path) => new RegExp('/supplier/clients').test(path),
	},
	{
		to: '/supplier/locations',
		title: 'Locations',
		icon: 'icons8-store',
		authorizedRoles: authorizedRolesCombinations.SUPPLIER_ONLY,
		checkActive: (path) => new RegExp('/supplier/locations').test(path),
	},
	{
		to: '/supplier/suppliers',
		title: 'Service Providers',
		icon: 'icons8-company-2',
		accessPermissionName: PERMISSION_NAMES.CAN_ACCESS_SUPPLIER_PAGE,
		authorizedRoles: authorizedRolesCombinations.SUPPLIER_ONLY,
		checkActive: (path) => new RegExp('/supplier/suppliers').test(path),
	},
	{
		to: '/supplier/assets',
		title: 'Assets',
		icon: 'icons8-bulldozer',
		authorizedRoles: authorizedRolesCombinations.SUPPLIER_ONLY,
		checkActive: (path) => new RegExp('/supplier/assets').test(path),
	},
	{
		to: '/supplier/meters',
		title: 'Meters',
		icon: 'icons8-dashboard',
		authorizedRoles: authorizedRolesCombinations.SUPPLIER_ONLY,
		checkActive: (path) => new RegExp('/supplier/meters').test(path),
	},
	{
		to: '/supplier/fieldTechs',
		title: 'Field Techs',
		icon: 'icons8-street-worker',
		authorizedRoles: authorizedRolesCombinations.SUPPLIER_ONLY,
		checkActive: (path) => new RegExp('/supplier/fieldTechs').test(path),
	},
	{
		to: '',
		title: 'Analytics',
		icon: 'icons8-line-chart',
		authorizedRoles: authorizedRolesCombinations.SUPPLIER_ONLY,
		accessPermissionName: PERMISSION_NAMES.ACCESS_ANALYTICS,
		children: [
			{
				to: '/supplier/analytics/lifecycle',
				title: 'Lifecycle',
				menuSvgIcon: BarChartMenuIcon,
				authorizedRoles: authorizedRolesCombinations.SUPPLIER_ONLY,
				checkActive: (path) => new RegExp('/supplier/analytics/lifecycle').test(path),
				showInMenu: () => posthog.isFeatureEnabled('life-cycle-analytics'),
			},
			{
				to: '/supplier/analytics/assets',
				title: 'Downtime',
				icon: 'icons8-sand-watch',
				authorizedRoles: authorizedRolesCombinations.SUPPLIER_ONLY,
				checkActive: (path) => new RegExp('/supplier/analytics/assets').test(path),
			},
			{
				to: '/supplier/analytics/fieldTechAnalytics',
				title: 'Tech Scorecards',
				icon: 'icons8-chart',
				authorizedRoles: authorizedRolesCombinations.SUPPLIER_ONLY,
				checkActive: (path) => new RegExp('/supplier/analytics/fieldTechAnalytics').test(path),
			},
			{
				to: '/supplier/analytics/spend',
				title: 'Spend',
				icon: 'icons8-fund-accounting',
				authorizedRoles: authorizedRolesCombinations.SUPPLIER_ONLY,
				checkActive: (path) => new RegExp('/supplier/analytics/spend').test(path),
			},
			{
				to: '/supplier/analytics/work',
				title: 'Work',
				icon: 'icons8-chart',
				authorizedRoles: authorizedRolesCombinations.SUPPLIER_ONLY,
				checkActive: (path) => new RegExp('/supplier/analytics/work').test(path),
			},
			{
				to: '/supplier/analytics/kpi',
				title: 'Scorecards',
				icon: 'icons8-scorecard',
				authorizedRoles: authorizedRolesCombinations.SUPPLIER_ONLY,
				checkActive: (path) => new RegExp('/supplier/analytics/kpi').test(path),
			},
		],
	},
	{
		to: '',
		title: 'Workflows',
		icon: 'icons8-mind-map',
		anyAccessPermissionNames: [PERMISSION_NAMES.VIEW_TROUBLESHOOTING, PERMISSION_NAMES.VIEW_BUDGET],
		authorizedRoles: authorizedRolesCombinations.SUPPLIER_ONLY,
		children: [
			{
				to: '/supplier/workflows/troubleshooting',
				title: 'Troubleshooting',
				icon: 'icons8-question-mark-in-a-chat-bubble',
				lockedIfManagedBySupplier: true,
				accessPermissionName: PERMISSION_NAMES.VIEW_TROUBLESHOOTING,
				authorizedRoles: authorizedRolesCombinations.SUPPLIER_ONLY,
				checkActive: (path) => new RegExp('/supplier/workflows/troubleshooting').test(path),
			},
			{
				to: '/supplier/workflows/budgets',
				title: 'Budgets',
				icon: 'icons8-cash-in-hand',
				accessPermissionName: PERMISSION_NAMES.VIEW_BUDGET,
				authorizedRoles: authorizedRolesCombinations.SUPPLIER_ONLY,
				checkActive: (path) => new RegExp('/supplier/workflows/budgets').test(path),
			},
			{
				to: '/supplier/workflows/cycleevents',
				title: 'Asset Cycle Count',
				icon: 'icons8-refresh',
				authorizedRoles: authorizedRolesCombinations.SUPPLIER_ADMIN_ONLY,
				checkActive: (path) => new RegExp('/supplier/workflows/cycleevents').test(path),
			},
		],
	},
	{
		to: '',
		title: 'Inventory',
		icon: 'icons8-warehouse-3',
		authorizedRoles: authorizedRolesCombinations.SUPPLIER_ONLY,
		unauthorizedCompanies: new Set([276322]),
		checkActive: (path) =>
			new RegExp(
				'/supplier/(stockLocations|parts|equipments|purchaseRequests|purchaseOrders|vendors|tools)'
			).test(path),
		children: [
			{
				to: '/supplier/stockLocations',
				title: 'Stock Locations',
				icon: 'icons8-warehouse-3',
				authorizedRoles: authorizedRolesCombinations.SUPPLIER_ONLY,
				checkActive: (path) => new RegExp('/supplier/stockLocations').test(path),
			},
			{
				to: '/supplier/parts',
				title: 'Parts',
				icon: 'icons8-screw',
				authorizedRoles: authorizedRolesCombinations.SUPPLIER_ONLY,
				checkActive: (path) => new RegExp('/supplier/parts').test(path),
			},
			{
				to: '/supplier/equipments',
				title: 'Equipment',
				icon: 'icons8-drill-2',
				authorizedRoles: authorizedRolesCombinations.SUPPLIER_ONLY,
				checkActive: (path) => new RegExp('/supplier/equipments').test(path),
			},
			{
				to: '/supplier/tools',
				title: 'Tools',
				icon: 'icons8-wrench',
				authorizedRoles: authorizedRolesCombinations.SUPPLIER_ONLY,
				checkActive: (path) => new RegExp('/supplier/tools').test(path),
				showInMenu: () => posthog.isFeatureEnabled('tools'),
			},
			{
				to: '/supplier/purchaseRequests',
				title: 'Purchase Requests',
				icon: 'icons8-add-shopping-cart',
				accessPermissionName: PERMISSION_NAMES.ACCESS_PR_AND_PO,
				authorizedRoles: authorizedRolesCombinations.SUPPLIER_ONLY,
				checkActive: (path) => new RegExp('/supplier/purchaseRequests').test(path),
			},
			{
				to: '/supplier/purchaseOrders',
				title: 'Purchase Orders',
				icon: 'icons8-buying',
				accessPermissionName: PERMISSION_NAMES.ACCESS_PR_AND_PO,
				authorizedRoles: authorizedRolesCombinations.SUPPLIER_ONLY,
				checkActive: (path) => new RegExp('/supplier/purchaseOrders').test(path),
			},
			{
				to: '/supplier/vendors',
				title: 'Vendors',
				icon: 'icons8-shop-2',
				authorizedRoles: authorizedRolesCombinations.SUPPLIER_ONLY,
				checkActive: (path) => new RegExp('/supplier/vendors').test(path),
			},
		],
	},
	{
		to: '/supplier/depreciation',
		title: 'Accounting',
		icon: 'icons8-bulldozer',
		accessPermissionName: PERMISSION_NAMES.ACCESS_DEPRECIATION,
		authorizedRoles: authorizedRolesCombinations.SUPPLIER_ONLY,
		checkActive: (path) => new RegExp('/supplier/depreciation').test(path),
	},
	{
		to: '/supplier/company/me',
		title: 'Company',
		icon: 'icons8-company-2',
		authorizedRoles: authorizedRolesCombinations.SUPPLIER_ONLY,
		checkActive: (path) => new RegExp('/supplier/company').test(path),
	},
	{
		to: '/supplier/admin',
		title: 'Admin',
		icon: 'icons8-multiple-users',
		authorizedRoles: authorizedRolesCombinations.SUPPLIER_ADMIN_ONLY,
		checkActive: (path) => new RegExp('/supplier/admin').test(path),
	},
	{
		to: '/supplier/approvalHierarchies',
		title: 'Approvals',
		icon: 'icons8-checkmark',
		authorizedRoles: authorizedRolesCombinations.SUPPLIER_ADMIN_ONLY,
		checkActive: (path) => new RegExp('/supplier/approvalHierarchies').test(path),
	},
];

export const MENU_ITEMS_BY_ROLE = {
	supplier: SUPPLIER_MENU_ITEMS,
	internal_supplier: INTERNAL_SUPPLIER_MENU_ITEMS,
	buyer: BUYER_MENU_ITEMS,
};

export const HOME_PAGE_BY_ROLE = {
	supplier: '/supplier/workOrders',
	buyer: '/buyer/workOrders',
};

export const DASHBOARD_HOME_PAGE_BY_ROLE = {
	supplier: '/supplier/dashboards/overview/all',
	buyer: '/buyer/dashboards/overview/all',
};

export const LOGIN_PAGE_BY_ROLE = {
	unknown: '/login',
	supplier: '/supplier/login',
	buyer: '/buyer/login',
};

export const NEW_ACCOUNT_SETUP_PAGE_BY_ROLE = {
	supplier: '/supplier/newAccountSetup',
	buyer: '/buyer/newAccountSetup',
};

export const SIGNUP_COMPLETION_PAGE_BY_ROLE = {
	supplier: '/supplier/signupCompletion',
	buyer: '/buyer/signupCompletion',
};

export const NEED_EMAIL_AUTHENTICATION_PAGE_BY_ROLE = {
	supplier: '/supplier/needEmailAuthentication',
	buyer: '/buyer/needEmailAuthentication',
};

export const getMenuItemsByRole = (role, featureFlags) => {
	return MENU_ITEMS_BY_ROLE[role];
};
export const getHomePageByRole = (role, featureFlags) => {
	return DASHBOARD_HOME_PAGE_BY_ROLE[role];
};
export const getNewAccountSetupPageByRole = (role) => NEW_ACCOUNT_SETUP_PAGE_BY_ROLE[role];
export const getSignupCompletionPageByRole = (role) => SIGNUP_COMPLETION_PAGE_BY_ROLE[role];
export const getNeedEmailAuthenticationPageByRole = (role) =>
	NEED_EMAIL_AUTHENTICATION_PAGE_BY_ROLE[role];
export const getLoginPageByRole = (role) => LOGIN_PAGE_BY_ROLE[role];
